<template>
  <section
    class="client-cabinet-block client-cabinet-current-card"
    :class="currentCardClasses"
    aria-labelledby="client-page-payment-title"
    data-testid="client-page-payment"
    data-field="to_pay next_charge booking.payments_type booking.ride booking.state client.balance company.currency_symbol company.payment_methods.stripe company.payment_methods.yandex_pay company.payment_minimums.yandex_pay"
  >
    <div class="client-cabinet-current-main">
      <div class="client-cabinet-current-title">
        <h2 id="client-page-payment-title">{{ $t("client_page.cabinet.payment.now") }}</h2>
        <span class="client-cabinet-state-badge">{{ $t(stateBadgeKey) }}</span>
      </div>

    <!-- Периодическое: вместо остатка — баланс клиента и ближайшее списание (REQ-09). -->
    <template v-if="isPeriodic">
      <template v-if="periodicDebtAmount > 0">
        <p class="client-cabinet-amount-label">{{ $t("client_page.cabinet.payment.debt") }}</p>
        <div class="client-cabinet-amount" data-testid="client-page-debt">{{ formatMoney(periodicDebtAmount) }}</div>
      </template>
      <p v-else class="client-cabinet-paid-title" data-testid="client-page-all-paid">{{ $t("client_page.cabinet.payment.all_paid") }}</p>
      <div class="client-cabinet-balance-row" data-testid="client-page-balance">
        <span>{{ $t("client_page.cabinet.payment.balance") }}</span>
        <strong>{{ formatMoney(client && client.balance) }}</strong>
      </div>
      <div v-if="nextCharge" class="client-cabinet-next-charge" data-testid="client-page-next-charge" data-field="next_charge.date next_charge.amount">
        <strong>{{ formatDate(nextCharge.date) }}</strong><br />
        {{ $t("client_page.cabinet.payment.next_charge_copy", { amount: formatMoney(nextCharge.amount) }) }}
      </div>
    </template>

    <template v-else-if="hasDue">
      <template v-if="allPaid">
        <p class="client-cabinet-paid-title" data-testid="client-page-all-paid">{{ $t("client_page.cabinet.payment.all_paid") }}</p>
      </template>
      <template v-else>
        <p class="client-cabinet-amount-label">{{ $t("client_page.cabinet.payment.to_pay") }}</p>
        <div class="client-cabinet-amount" data-testid="client-page-to-pay">{{ formatMoney(due) }}</div>
      </template>

      <div v-if="cost > 0" class="mb-3" data-testid="client-page-paid-progress">
        <div class="text-body-2 mb-1">{{ $t("client_page.cabinet.payment.paid_of", { paid: formatMoney(paid), total: formatMoney(cost) }) }}</div>
        <v-progress-linear
          :value="paidPercent"
          height="8"
          rounded
          color="primary"
          :aria-label="$t('client_page.cabinet.payment.paid_of', { paid: formatMoney(paid), total: formatMoney(cost) })"
        ></v-progress-linear>
      </div>
    </template>

    <div
      v-if="deposit"
      class="client-cabinet-row mb-3"
      data-testid="client-page-deposit"
      data-field="booking.counts[].group booking.counts[].operation booking.counts[].completed"
    >
      <span>{{ $t("client_page.cabinet.payment.deposit") }}</span>
      <span>
        <strong>{{ formatMoney(deposit.amount) }}</strong>
        <template v-if="deposit.state === 'returned'"> · {{ $t("client_page.cabinet.payment.deposit_states.returned") }}</template>
        <template v-else-if="deposit.state === 'partly_returned'"> · {{ $t("client_page.cabinet.payment.deposit_states.returned_part", { amount: formatMoney(deposit.returned) }) }}</template>
      </span>
    </div>

    <!-- «Оплатить X»: X — сумма сервера; способ ниже своего минимума не предлагается. -->
    <template v-if="hasPrimaryDebt">
      <v-btn
        v-if="primaryMethods.length"
        color="primary"
        block
        x-large
        depressed
        data-testid="client-page-pay"
        @click="payDue"
      >{{ $t("client_page.cabinet.payment.pay", { amount: formatMoney(effectiveDebt) }) }}</v-btn>

      <v-btn
        v-if="isPeriodic && nextChargeAmount > 0 && combinedMethods.length"
        outlined
        block
        min-height="48"
        class="mt-2"
        data-testid="client-page-pay-next"
        @click="payCombined"
      >{{ $t("client_page.cabinet.payment.pay_debt_and_charge", { amount: formatMoney(combinedAmount) }) }}</v-btn>

      <p v-if="!hasAnyDebtMethod" class="text-body-2 mb-2" data-testid="client-page-no-methods">{{ $t("client_page.cabinet.payment.no_methods") }}</p>

      <div v-if="!hasAnyDebtMethod" class="client-cabinet-offline-box" data-testid="client-page-offline-payment">
        <strong>{{ $t("client_page.cabinet.payment.offline_title") }}</strong>
        <p>{{ $t("client_page.cabinet.payment.offline_copy") }}</p>
        <a v-if="company && company.phone" :href="phoneHref">{{ $t("client_page.cabinet.payment.call_company", { phone: company.phone }) }}</a>
      </div>

      <div v-if="chooser" class="mt-3" role="group" :aria-label="$t('client_page.cabinet.payment.choose_method')">
        <div class="text-body-2 mb-2">{{ $t("client_page.cabinet.payment.choose_method") }}</div>
        <v-btn
          v-for="method in chooserMethods"
          :key="method.id"
          outlined
          block
          min-height="44"
          class="mb-2"
          :data-testid="`client-page-method-${method.id}`"
          @click="pay(method.id, chooserAmount)"
        >{{ $t(method.label) }}</v-btn>
      </div>
    </template>

    <!-- Своя сумма — вторичным действием; у периодического — пополнение баланса, раскрыто сразу. -->
    <template v-if="offerCustom">
      <v-btn
        v-if="!isPeriodic"
        text
        color="primary"
        min-height="44"
        class="mt-2 px-0"
        :aria-expanded="showCustom ? 'true' : 'false'"
        data-testid="client-page-other-amount"
        @click="showCustom = !showCustom"
      >{{ $t("client_page.cabinet.payment.other_amount") }}</v-btn>
      <div v-if="showCustom || isPeriodic" class="mt-2">
        <div v-if="isPeriodic" class="text-body-2 mb-2">{{ $t("client_page.cabinet.payment.top_up") }}</div>
        <v-text-field
          v-model="customAmount"
          :label="$t('client_page.cabinet.payment.amount')"
          :suffix="symbol"
          inputmode="decimal"
          outlined
          hide-details="auto"
          data-testid="client-page-custom-amount"
        ></v-text-field>
        <div class="mt-2">
          <!-- Пополнение периодического: первый способ — главное действие (заливка, «Пополнить картой»), как «Оплатить X». -->
          <template v-for="(method, index) in methods">
            <v-btn
              :key="method.id"
              :outlined="!(isPeriodic && index === 0)"
              :depressed="isPeriodic && index === 0"
              :color="isPeriodic && index === 0 ? 'primary' : undefined"
              block
              min-height="44"
              class="mb-1"
              :disabled="!customAllowed(method)"
              :data-testid="`client-page-custom-method-${method.id}`"
              @click="pay(method.id, customValue)"
            >{{ $t(isPeriodic ? method.topUpLabel : method.label) }}</v-btn>
            <p v-if="method.min && customValue > 0 && customValue < method.min" :key="`${method.id}-min`" class="text-caption mb-2">
              {{ $t("client_page.cabinet.payment.below_minimum", { method: $t(method.label), amount: formatMoney(method.min) }) }}
            </p>
          </template>
        </div>
      </div>
    </template>

    </div>
    <div v-if="hasPrimaryDebt && primaryMethods.length" class="client-cabinet-current-foot">
      {{ $t("client_page.cabinet.payment.secure") }}
    </div>

    <stripe-payment-dialog-public
      v-if="hasStripe"
      ref="stripeDialog"
      :booking-id="bookingId"
      :last-name="lastName"
      :amount="stripeAmount"
      :company="company"
    ></stripe-payment-dialog-public>

    <!-- Yandex Pay: заказ с адресами возврата кабинета; операция — во вкладку до перехода к банку. -->
    <v-dialog
      :value="yandex.show"
      :persistent="yandex.loading"
      content-class="client-cabinet-dialog"
      max-width="520px"
      @input="onYandexInput"
    >
      <v-card data-testid="client-page-yandex-dialog">
        <v-card-title><h2 class="text-h6">{{ $t("client_page.cabinet.pay_dialog.yandex_title") }}</h2></v-card-title>
        <v-card-text>
          <p class="text-subtitle-1 mb-4">{{ $t("client_page.cabinet.pay_dialog.amount_line", { amount: formatMoney(yandex.amount) }) }}</p>
          <p v-if="yandex.error" class="client-cabinet-error mb-3" role="alert" data-testid="client-page-yandex-error">{{ yandex.error }}</p>
          <template v-if="yandex.url">
            <p class="mb-2">{{ $t("client_page.cabinet.pay_dialog.link_hint") }}</p>
            <a :href="yandex.url" target="_blank" rel="noopener" class="client-cabinet-link" data-testid="client-page-yandex-link">
              {{ $t("client_page.cabinet.pay_dialog.open_again") }}
            </a>
            <div v-if="yandex.qr" class="text-center mt-4">
              <img :src="yandex.qr" :alt="$t('client_page.cabinet.pay_dialog.qr_alt')" style="max-width: 250px; width: 100%;" />
            </div>
          </template>
        </v-card-text>
        <v-card-actions class="flex-wrap">
          <v-btn text min-height="44" @click="closeYandex">{{ $t("client_page.cabinet.pay_dialog.close") }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!yandex.url"
            color="primary"
            depressed
            min-height="44"
            :loading="yandex.loading"
            :disabled="yandex.loading"
            data-testid="client-page-yandex-submit"
            @click="createYandexOrder"
          >{{ $t("client_page.cabinet.pay_dialog.go") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
// FT-200 STEP-05 (REQ-09, U6): блок «Оплата» кабинета клиента.
//
// «К оплате X» — to_pay сервера (Fleet::BookingMoney.to_pay, та же формула, что в карточке сотрудника); кабинет её не
// пересчитывает. X ≤ 0 — «Всё оплачено», кнопок нет. Полоса «оплачено Y из Z»: Z — начисления с залогом (та же
// формула cost), Y = Z − X — так полоса не расходится с суммой сервера (мультивалютные платежи сервер считает по курсу).
// Способы — company.payment_methods; способ, у которого сумма ниже его минимума (payment_minimums), не предлагается.
// Минимума Stripe ответ не знает: его отказ покажет диалог понятным текстом.
// Периодическое бронирование — баланс клиента и ближайшее списание (дата и сумма — строками сервера) и пополнение
// своей суммой.
import QRCode from "qrcode";
import StripePaymentDialogPublic from "../StripePaymentDialogPublic";
import clientCabinetReturn from "@/utils/clientCabinetReturn";
import clientCabinetSession from "@/utils/clientCabinetSession";
import { openPaymentUrl } from "@/utils/tinkoffCard";
import { telHref } from "@/lib/phone/display";
import {
  bookingCost, cabinetDebtAmount, combinedPaymentAmount, depositInfo, formatCabinetDate, money, parseAmount,
  paymentErrorKey, reportServerError, roundMoney,
} from "@/utils/clientCabinetView";

export const YANDEX_ORDERS_PATH = "/api/v1/integrations/public_yandex_pays/orders";

const METHODS = [
  { id: "stripe", label: "client_page.cabinet.payment.method_stripe", topUpLabel: "client_page.cabinet.payment.top_up_stripe" },
  { id: "yandex_pay", label: "client_page.cabinet.payment.method_yandex_pay", topUpLabel: "client_page.cabinet.payment.top_up_yandex_pay" },
];

const emptyYandex = () => ({ show: false, amount: null, url: null, qr: null, error: null, loading: false });

export default {
  name: "PaymentBlock",
  components: { StripePaymentDialogPublic },
  props: {
    booking: { type: Object, required: true },
    company: { type: Object, default: null },
    client: { type: Object, default: null },
    toPay: { type: [String, Number], default: null },
    nextCharge: { type: Object, default: null },
    bookingId: { type: [String, Number], required: true },
    lastName: { type: String, required: true },
  },
  data() {
    return {
      chooser: false,
      chooserAmount: 0,
      showCustom: false,
      customAmount: "",
      prefilledAmount: "",
      stripeAmount: null,
      yandex: emptyYandex(),
    };
  },
  computed: {
    symbol() {
      return (this.company && this.company.currency_symbol) || "";
    },
    phoneHref() {
      return telHref((this.company && this.company.phone) || "");
    },
    isPeriodic() {
      return !!this.booking && this.booking.payments_type === "periodic";
    },
    due() {
      return parseAmount(this.toPay);
    },
    periodicDebtAmount() {
      if (!this.isPeriodic) return 0;
      const amount = cabinetDebtAmount(this.booking, this.toPay, this.client && this.client.balance);
      return Number.isNaN(amount) ? 0 : amount;
    },
    effectiveDebt() {
      return this.isPeriodic ? this.periodicDebtAmount : Math.max(0, this.due);
    },
    hasPrimaryDebt() {
      return this.isPeriodic ? this.periodicDebtAmount > 0 : this.hasDue && !this.allPaid;
    },
    // to_pay нет — сервер не смог посчитать (ключ null): блок суммы не показывается вовсе.
    hasDue() {
      return !Number.isNaN(this.due);
    },
    allPaid() {
      if (this.isPeriodic) return this.hasDue && roundMoney(this.due) <= 0 && this.periodicDebtAmount <= 0;
      return this.hasDue && !(roundMoney(this.due) > 0);
    },
    currentCardClasses() {
      return {
        "client-cabinet-current-card--debt": this.hasPrimaryDebt,
        "client-cabinet-current-card--paid": this.allPaid,
      };
    },
    stateBadgeKey() {
      if (this.hasPrimaryDebt) return "client_page.cabinet.payment.has_debt";
      if (this.allPaid) return "client_page.cabinet.payment.ready";
      return "client_page.cabinet.payment.to_pay";
    },
    cost() {
      return bookingCost(this.booking);
    },
    paid() {
      return roundMoney(Math.max(0, this.cost - Math.max(0, this.due)));
    },
    paidPercent() {
      return this.cost > 0 ? Math.min(100, (this.paid / this.cost) * 100) : 0;
    },
    deposit() {
      return depositInfo(this.booking);
    },
    // Способы компании с минимумами; payment_methods нет (сбой ключа) — способов нет.
    methods() {
      const enabled = (this.company && this.company.payment_methods) || {};
      const minimums = (this.company && this.company.payment_minimums) || {};
      return METHODS.filter((method) => enabled[method.id]).map((method) => {
        const min = parseAmount(minimums[method.id]);
        return { ...method, min: Number.isNaN(min) ? null : min };
      });
    },
    dueMethods() {
      return this.hasDue ? this.methodsFor(this.due) : [];
    },
    primaryMethods() {
      return this.methodsFor(this.effectiveDebt);
    },
    nextChargeAmount() {
      const amount = parseAmount(this.nextCharge && this.nextCharge.amount);
      return Number.isNaN(amount) || amount <= 0 ? 0 : roundMoney(amount);
    },
    combinedAmount() {
      return combinedPaymentAmount(this.periodicDebtAmount, this.nextChargeAmount);
    },
    combinedMethods() {
      return this.methodsFor(this.combinedAmount);
    },
    hasAnyDebtMethod() {
      return this.primaryMethods.length > 0 || (this.isPeriodic && this.combinedMethods.length > 0);
    },
    chooserMethods() {
      return this.methodsFor(this.chooserAmount);
    },
    hasStripe() {
      return this.methods.some((method) => method.id === "stripe");
    },
    offerCustom() {
      if (!this.methods.length) return false;
      return this.isPeriodic || (this.hasDue && !this.allPaid);
    },
    customValue() {
      const amount = parseAmount(this.customAmount);
      return Number.isNaN(amount) ? 0 : roundMoney(amount);
    },
  },
  watch: {
    // Периодическое: пополнение раскрыто сразу, сумма по умолчанию — ближайшее списание. Новое списание после
    // перечитывания меняет сумму, только если клиент не вводил свою.
    "nextCharge.amount": {
      immediate: true,
      handler(amount) {
        if (!this.isPeriodic) return;
        const value = parseAmount(amount);
        const prefill = value > 0 ? String(roundMoney(value)) : "";
        if (this.customAmount === "" || this.customAmount === this.prefilledAmount) this.customAmount = prefill;
        this.prefilledAmount = prefill;
      },
    },
  },
  methods: {
    formatMoney(value) {
      return money(value, this.symbol);
    },
    formatDate(value) {
      return formatCabinetDate(value, this.$i18n && this.$i18n.locale);
    },
    methodsFor(amount) {
      if (!(amount > 0)) return [];
      return this.methods.filter((method) => !method.min || amount >= method.min);
    },
    customAllowed(method) {
      const amount = this.customValue;
      return amount > 0 && (!method.min || amount >= method.min);
    },
    payDue() {
      this.payAmount(this.effectiveDebt);
    },
    payCombined() {
      this.payAmount(this.combinedAmount);
    },
    payAmount(amount) {
      const available = this.methodsFor(amount);
      if (available.length === 1) {
        this.pay(available[0].id, amount);
      } else {
        this.chooserAmount = amount;
        this.chooser = !this.chooser;
      }
    },
    pay(methodId, amount) {
      const value = roundMoney(amount);
      if (methodId === "stripe") {
        this.stripeAmount = value;
        // Сумма диалога — prop: открыть после того, как он её получит.
        this.$nextTick(() => {
          if (this.$refs.stripeDialog) this.$refs.stripeDialog.open();
        });
      } else if (methodId === "yandex_pay") {
        this.yandex = { ...emptyYandex(), show: true, amount: value };
      }
    },
    closeYandex() {
      this.yandex = emptyYandex();
    },
    // REQ-13: Esc и подложка (Vuetify закрывает не persistent диалог) — как «Закрыть».
    onYandexInput(open) {
      if (!open) this.closeYandex();
    },
    createYandexOrder() {
      if (this.yandex.loading) return Promise.resolve();
      const payload = { booking_id: this.bookingId, last_name: this.lastName, amount: this.yandex.amount };
      const urls = clientCabinetReturn.urls("payment", this.company);
      if (urls) {
        payload.success_url = urls.success_url;
        payload.error_url = urls.error_url;
        payload.abort_url = urls.abort_url;
      }
      this.yandex.loading = true;
      this.yandex.error = null;
      return this.axios
        .post(YANDEX_ORDERS_PATH, payload)
        .then((response) => {
          const data = (response && response.data) || {};
          if (!data.payment_url) {
            this.yandex.error = this.$t("client_page.cabinet.pay_dialog.errors.failed");
            return;
          }
          // Операция — до ссылки и QR: по ней страница возврата узнает итог, в том числе оплаты с другого устройства.
          clientCabinetSession.startOperation({ flow: "payment", provider: "yandex_pay", orderId: data.order_id });
          this.yandex.url = data.payment_url;
          QRCode.toDataURL(data.payment_url, { width: 250, margin: 2 })
            .then((dataUrl) => { this.yandex.qr = dataUrl; })
            .catch(() => { this.yandex.qr = null; });
          openPaymentUrl(data.payment_url);
        })
        .catch((error) => {
          this.yandex.error = this.$t(paymentErrorKey(error));
          reportServerError(this, "ClientCabinet: заказ Yandex Pay", error);
        })
        .then(() => {
          this.yandex.loading = false;
        });
    },
  },
};
</script>
